// composables/useCatalogStructure.js
import { computed } from 'vue'

export function useCatalogStructure(
  catalog,
  evaluations,
  ratings,
  currentStep,
) {
  // Helper: Check ob Action bewertet werden muss (für Step 2)
  function shouldRateAction(action, evals) {
    const evaluation = evals[action.id]
    return (
      evaluation &&
      evaluation.value !== 'implemented' &&
      evaluation.value !== 'unrequired'
    )
  }

  // Helper: Check ob Action in Step 1 completed ist (hat value)
  function isActionAnsweredInStep1(action, evals) {
    const evaluation = evals[action.id]
    return !!evaluation?.value
  }

  // Helper: Check ob Action in Step 2 completed ist
  function isActionCompletedInStep2(action, evals, rats) {
    const evaluation = evals[action.id]

    if (!evaluation) return false

    // Wenn implemented oder unrequired → automatisch completed
    if (
      evaluation.value === 'implemented' ||
      evaluation.value === 'unrequired'
    ) {
      return true
    }

    // Sonst: Braucht Rating (effort UND impact)
    const rating = rats[action.id]
    return !!(rating?.effort && rating?.impact)
  }

  // Generische Enrichment-Funktion
  function enrichCatalogWithProgress(filterFn, progressFn) {
    if (!catalog.value?.areas) {
      return { areas: [] }
    }

    const evals = evaluations.value || {}
    const rats = ratings.value || {}

    const enrichedAreas = catalog.value.areas
      .map((area) => {
        const enrichedCategories = area.categories
          .map((category) => {
            const enrichedObjectives =
              category.objectives
                ?.map((objective) => {
                  // Filter Actions (für Step 2 nur die zu bewertenden)
                  const filteredActions =
                    objective.actions?.filter((action) =>
                      filterFn(action, evals),
                    ) || []

                  // Progress für Objective
                  const objectiveTotal = filteredActions.length
                  const objectiveCompleted = filteredActions.filter(
                    (action) => progressFn(action, evals, rats), // ← beide übergeben
                  ).length

                  return {
                    ...objective,
                    actions: filteredActions,
                    progress: {
                      total: objectiveTotal,
                      completed: objectiveCompleted,
                      percentage:
                        objectiveTotal > 0
                          ? Math.round(
                              (objectiveCompleted / objectiveTotal) * 100,
                            )
                          : 0,
                    },
                  }
                })
                .filter((objective) => objective.actions.length > 0) || []

            // Progress für Category
            const categoryTotal = enrichedObjectives.reduce(
              (sum, obj) => sum + obj.progress.total,
              0,
            )
            const categoryCompleted = enrichedObjectives.reduce(
              (sum, obj) => sum + obj.progress.completed,
              0,
            )

            return {
              ...category,
              objectives: enrichedObjectives,
              progress: {
                total: categoryTotal,
                completed: categoryCompleted,
                percentage:
                  categoryTotal > 0
                    ? Math.round((categoryCompleted / categoryTotal) * 100)
                    : 0,
              },
            }
          })
          .filter((category) => category.objectives.length > 0)

        // Progress für Area
        const areaTotal = enrichedCategories.reduce(
          (sum, cat) => sum + cat.progress.total,
          0,
        )
        const areaCompleted = enrichedCategories.reduce(
          (sum, cat) => sum + cat.progress.completed,
          0,
        )

        return {
          ...area,
          categories: enrichedCategories,
          progress: {
            total: areaTotal,
            completed: areaCompleted,
            percentage:
              areaTotal > 0 ? Math.round((areaCompleted / areaTotal) * 100) : 0,
          },
        }
      })
      .filter((area) => area.categories.length > 0)

    return {
      ...catalog.value,
      areas: enrichedAreas,
    }
  }

  // Katalog für Evaluation Step (Step 1)
  // Filter: ALLE Actions
  // Progress: Completed wenn value vorhanden
  const catalogForEvaluation = computed(() => {
    return enrichCatalogWithProgress(
      () => true, // Alle Actions zeigen
      isActionAnsweredInStep1, // Completed = hat value
    )
  })

  // Katalog für Rating Step (Step 2)
  // Filter: Nur Actions die NICHT implemented/unrequired sind
  // Progress: Completed wenn (implemented/unrequired) ODER (rating.effort UND rating.impact)
  const catalogForRating = computed(() => {
    return enrichCatalogWithProgress(
      shouldRateAction, // Nur Actions die bewertet werden müssen
      isActionCompletedInStep2, // Completed = implemented/unrequired ODER hat Rating
    )
  })

  // Katalog je nach aktuellem Step
  const catalogForCurrentStep = computed(() => {
    if (currentStep.value === 'rating') {
      return catalogForRating.value
    }
    return catalogForEvaluation.value
  })

  return {
    catalogForCurrentStep,
    catalogForEvaluation,
    catalogForRating,
  }
}
